import { useState, useRef, useEffect } from "react";
import { PublicClientApplication } from "@azure/msal-browser";
import HelpPage from "./HelpPage";
import LegalPage from "./LegalPage";
import Profile from "./Profile";
import { api } from "../api";
import "../styles/LoginPage.css";

const AUDIT_DEADLINE = new Date(process.env.REACT_APP_AUDIT_DEADLINE || '2026-09-30');

// ─── MSAL CONFIG ──────────────────────────────────────────────────────────────
const MSAL_CONFIG = {
  auth: {
    clientId: process.env.REACT_APP_AZURE_CLIENT_ID,
    authority: process.env.REACT_APP_AZURE_AUTHORITY,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    knownAuthorities: [process.env.REACT_APP_AZURE_KNOWN_AUTHORITY],
  },
  cache: { cacheLocation: "localStorage" },
};

function getAccountDisplay(account) {
  if (!account) return { name: "", email: "", initial: "?" };
  const name =
    account.name ||
    account.idTokenClaims?.name ||
    account.username?.split("@")[0] ||
    "";
  const email =
    account.idTokenClaims?.email ||
    account.idTokenClaims?.preferred_username ||
    account.idTokenClaims?.emails?.[0] ||
    account.username ||
    account.preferred_username ||
    "";
  return { name, email, initial: (name || email || "?")[0].toUpperCase() };
}

// ─── ICONS ───────────────────────────────────────────────────────────────────
const LogoIcon = () => (
  <svg viewBox="0 0 16 16" fill="none">
    <rect
      x="1"
      y="1"
      width="6"
      height="6"
      rx="1.5"
      fill="white"
      opacity=".95"
    />
    <rect
      x="9"
      y="1"
      width="6"
      height="6"
      rx="1.5"
      fill="white"
      opacity=".55"
    />
    <rect
      x="1"
      y="9"
      width="6"
      height="6"
      rx="1.5"
      fill="white"
      opacity=".55"
    />
    <rect
      x="9"
      y="9"
      width="6"
      height="6"
      rx="1.5"
      fill="white"
      opacity=".95"
    />
  </svg>
);
const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18">
    <path
      fill="#4285F4"
      d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
    />
    <path
      fill="#34A853"
      d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"
    />
    <path
      fill="#FBBC05"
      d="M3.964 10.706A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"
    />
    <path
      fill="#EA4335"
      d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z"
    />
  </svg>
);

// ─── DEADLINE COUNTDOWN ────────────────────────────────────────────────────────
function DeadlineCountdown() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffMs = AUDIT_DEADLINE.getTime() - today.getTime();
  const daysLeft = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
  const deadlineFmt = AUDIT_DEADLINE.toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
  return (
    <div className="lp-dc-card">
      <div className="lp-dc-num">{daysLeft}</div>
      <div className="lp-dc-label">DAYS LEFT TO FILE</div>
      <div className="lp-dc-until">until {deadlineFmt}</div>
    </div>
  );
}

// ─── INDIAN FLAG WATERMARK ────────────────────────────────────────────────────
function IndianFlagWatermark() {
  const cx = 250, cy = 250;
  const spokes = Array.from({ length: 24 }, (_, i) => {
    const a = (i * 15 * Math.PI) / 180;
    return {
      x1: cx + 38 * Math.cos(a), y1: cy + 38 * Math.sin(a),
      x2: cx + 88 * Math.cos(a), y2: cy + 88 * Math.sin(a),
    };
  });
  return (
    <div className="lp-flag-watermark" aria-hidden="true">
      <svg viewBox="0 0 500 500" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <clipPath id="lp-flag-clip">
            <circle cx="250" cy="250" r="222" />
          </clipPath>
        </defs>
        <rect x="0" y="0"   width="500" height="167" fill="#FF9933" clipPath="url(#lp-flag-clip)" />
        <rect x="0" y="167" width="500" height="166" fill="#FFFFFF" clipPath="url(#lp-flag-clip)" />
        <rect x="0" y="333" width="500" height="167" fill="#138808" clipPath="url(#lp-flag-clip)" />
        <circle cx="250" cy="250" r="222" fill="none" stroke="#ccc" strokeWidth="1.5" />
        <circle cx="250" cy="250" r="90"  fill="none" stroke="#000080" strokeWidth="3" />
        <circle cx="250" cy="250" r="13"  fill="#000080" />
        {spokes.map((s, i) => (
          <line key={i} x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2}
            stroke="#000080" strokeWidth="1.8" strokeLinecap="round" />
        ))}
      </svg>
    </div>
  );
}

// ─── OTP TAB ──────────────────────────────────────────────────────────────────
function OtpTab({ onSuccess }) {
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const startCountdown = () => {
    setCountdown(60);
    const t = setInterval(
      () =>
        setCountdown((c) => {
          if (c <= 1) {
            clearInterval(t);
            return 0;
          }
          return c - 1;
        }),
      1000,
    );
  };
  const handleSendOtp = async () => {
    if (mobile.length < 10) {
      setError("Please enter a valid 10-digit mobile number.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await new Promise((r) => setTimeout(r, 900));
      setOtpSent(true);
      startCountdown();
    } catch {
      setError("Could not send OTP.");
    } finally {
      setLoading(false);
    }
  };
  const handleVerify = async () => {
    if (otp.length < 4) {
      setError("Please enter the OTP sent to your mobile.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await new Promise((r) => setTimeout(r, 900));
      onSuccess({ method: "otp", mobile });
    } catch {
      setError("Incorrect OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div>
      {error && <div className="lp-error-box">{error}</div>}
      <div className="lp-field">
        <label>Mobile number</label>
        <div className="lp-otp-row">
          <div className="lp-field-wrap">
            <span className="lp-field-prefix">+91</span>
            <input
              type="tel"
              maxLength={10}
              placeholder="98765 43210"
              value={mobile}
              onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
              disabled={otpSent}
            />
          </div>
          <button
            className="lp-send-btn"
            onClick={handleSendOtp}
            disabled={loading || countdown > 0 || mobile.length < 10}
          >
            {loading && !otpSent ? "Sending…" : otpSent ? "Resend" : "Send OTP"}
          </button>
        </div>
        {countdown > 0 && (
          <div className="lp-countdown">Resend in {countdown}s</div>
        )}
      </div>
      {otpSent && (
        <div className="lp-field">
          <label>One-time password</label>
          <div className="lp-field-wrap">
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="6-digit code"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              autoFocus
            />
          </div>
        </div>
      )}
      <button
        className="lp-primary-btn"
        onClick={otpSent ? handleVerify : handleSendOtp}
        disabled={loading || (!otpSent && mobile.length < 10)}
      >
        {loading ? <span className="lp-spinner" /> : null}
        {loading ? "Please wait…" : otpSent ? "Verify & sign in →" : "Send OTP"}
      </button>
    </div>
  );
}

// ─── GMAIL TAB ────────────────────────────────────────────────────────────────
function GmailTab({ onSuccess, forceSwitch = false }) {
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState("");
  const [cachedAccount, setCachedAccount] = useState(null);
  const msalRef = useRef(null);
  useEffect(() => {
    Object.keys(localStorage)
      .filter(
        (k) =>
          (k.includes("msal") ||
            k.includes("ciamlogin") ||
            k.includes("login.windows.net")) &&
          !k.includes("account") &&
          !k.includes("idtoken") &&
          !k.includes("accesstoken") &&
          (k.includes("interaction.status") ||
            k.includes("request.params") ||
            k.includes("nonce") ||
            k.includes("state") ||
            k.includes("pkce")),
      )
      .forEach((k) => localStorage.removeItem(k));
    const instance = new PublicClientApplication(MSAL_CONFIG);
    instance
      .initialize()
      .then(() => {
        msalRef.current = instance;
        return instance.handleRedirectPromise();
      })
      .then((result) => {
        setReady(true);
        if (result?.account) {
          localStorage.setItem(
            "auditpros_user",
            JSON.stringify({ method: "gmail", account: result.account }),
          );
          onSuccess({ method: "gmail", account: result.account });
        } else if (!forceSwitch) {
          const accounts = instance.getAllAccounts();
          if (accounts.length > 0) setCachedAccount(accounts[0]);
        }
      })
      .catch((e) => {
        setReady(true);
        if (e.errorCode !== "user_cancelled")
          setError(`Sign-in failed: ${e.errorCode} — ${e.message}`);
      });
  }, [forceSwitch]);
  const handleContinueAs = () => {
    if (!cachedAccount) return;
    localStorage.setItem(
      "auditpros_user",
      JSON.stringify({ method: "gmail", account: cachedAccount }),
    );
    onSuccess({ method: "gmail", account: cachedAccount });
  };
  const handleSignInWithPicker = async () => {
    if (!msalRef.current) return;
    setError("");
    setLoading(true);
    setRedirecting(true);
    try {
      await msalRef.current.loginRedirect({
        scopes: ["openid", "profile", "email"],
        prompt: "select_account",
        loginHint: undefined,
        extraQueryParameters: { prompt: "select_account" },
      });
    } catch (e) {
      setRedirecting(false);
      setLoading(false);
      if (e.errorCode !== "user_cancelled")
        setError(`Sign-in failed: ${e.errorCode} — ${e.message}`);
    }
  };
  if (redirecting)
    return (
      <div className="lp-redirecting">
        <div className="lp-redirecting-spinner" />
        <p className="lp-redirecting-text">Redirecting to Google…</p>
      </div>
    );
  const { name, email, initial } = getAccountDisplay(cachedAccount);
  return (
    <div>
      {error && <div className="lp-error-box">{error}</div>}
      {cachedAccount && !forceSwitch ? (
        <>
          <p
            style={{
              fontSize: 13,
              color: "var(--mid)",
              marginBottom: 12,
              lineHeight: 1.5,
            }}
          >
            Previously signed in as:
          </p>
          <div className="lp-account-chip">
            <div className="lp-account-avatar">{initial}</div>
            <div className="lp-account-info">
              {name && <div className="lp-account-name">{name}</div>}
              {email && <div className="lp-account-email">{email}</div>}
            </div>
            <button
              className="lp-switch-link"
              onClick={() => setCachedAccount(null)}
            >
              Switch
            </button>
          </div>
          <button
            className="lp-primary-btn"
            onClick={handleContinueAs}
            disabled={!ready}
          >
            {!ready ? <span className="lp-spinner" /> : null}Continue as{" "}
            {name?.split(" ")[0] || email?.split("@")[0] || "this account"} →
          </button>
          <div className="lp-divider">
            <div className="lp-divider-line" />
            <span className="lp-divider-text">or use a different account</span>
            <div className="lp-divider-line" />
          </div>
          <button
            className="lp-google-btn"
            onClick={handleSignInWithPicker}
            disabled={loading || !ready}
          >
            {loading ? (
              <span
                className="lp-spinner"
                style={{
                  borderColor: "rgba(0,0,0,0.15)",
                  borderTopColor: "var(--ink)",
                }}
              />
            ) : (
              <GoogleIcon />
            )}
            Use a different Google account
          </button>
        </>
      ) : (
        <>
          <p
            style={{
              fontSize: 14,
              color: "var(--mid)",
              marginBottom: 24,
              lineHeight: 1.6,
            }}
          >
            Sign in securely with your Google account. We only access your name
            and email — nothing else.
          </p>
          <button
            className="lp-google-btn"
            onClick={handleSignInWithPicker}
            disabled={loading || !ready}
          >
            {loading ? (
              <span
                className="lp-spinner"
                style={{
                  borderColor: "rgba(0,0,0,0.15)",
                  borderTopColor: "var(--ink)",
                }}
              />
            ) : (
              <GoogleIcon />
            )}
            {!ready && !error
              ? "Loading…"
              : loading
                ? "Redirecting…"
                : "Continue with Google"}
          </button>
        </>
      )}
    </div>
  );
}

// ─── LOGIN CARD ───────────────────────────────────────────────────────────────
function LoginCard({ forceSwitch, onSuccess, onShowHelp }) {
  const [tab, setTab] = useState("gmail");
  return (
    <div className="lp-login-card">
      <div className="lp-card-eyebrow">
        {forceSwitch ? "Switch account" : "CA's · Clients · Accountants"}
      </div>
      <div className="lp-card-title">Sign in to Audit PrOS</div>
      <div className="lp-card-punchline">
        Smarter Audits. Cleaner Books. One Platform.
      </div>
      <p className="lp-card-sub">
        {forceSwitch
          ? "Choose the Google account you'd like to use."
          : "One sign-in for CA firms, business owners, and in-house accountants."}
      </p>
      {!forceSwitch && (
        <div className="lp-tab-row">
          <button
            className={`lp-tab-btn${tab === "otp" ? " active" : ""}`}
            onClick={() => setTab("otp")}
          >
            Mobile OTP
          </button>
          <button
            className={`lp-tab-btn${tab === "gmail" ? " active" : ""}`}
            onClick={() => setTab("gmail")}
          >
            Google
          </button>
        </div>
      )}
      {tab === "otp" && !forceSwitch ? (
        <OtpTab onSuccess={onSuccess} />
      ) : (
        <GmailTab onSuccess={onSuccess} forceSwitch={forceSwitch} />
      )}
      {!forceSwitch && (
        <>
          <div className="lp-divider">
            <div className="lp-divider-line" />
            <span className="lp-divider-text">or</span>
            <div className="lp-divider-line" />
          </div>
          {tab === "otp" ? (
            <button className="lp-google-btn" onClick={() => setTab("gmail")}>
              <GoogleIcon />
              Continue with Google instead
            </button>
          ) : (
            <button
              className="lp-google-btn"
              onClick={() => setTab("otp")}
              style={{ fontSize: 13 }}
            >
              Use mobile OTP instead
            </button>
          )}
        </>
      )}
      <div className="lp-card-footer">
        {forceSwitch ? (
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              window.history.back();
            }}
          >
            ← Back
          </a>
        ) : (
          <>
            <span>
              New user?&nbsp;<a href="/register">Create an account</a>
            </span>
            &nbsp;·&nbsp;
            <a
              onClick={(e) => {
                e.preventDefault();
                onShowHelp();
              }}
            >
              Need help?
            </a>
          </>
        )}
      </div>
    </div>
  );
}

// ─── FEATURE DATA ─────────────────────────────────────────────────────────────
const FEAT_TABS = [
  { id: "ai", label: "AI Audit Engine" },
  { id: "tally", label: "Tally Accountants" },
  { id: "client", label: "CA ↔ Client" },
  { id: "compliance", label: "Compliance" },
  { id: "team", label: "Team & Workflow" },
];

const FEAT_PANELS = {
  ai: [
    {
      hl: true,
      bg: "var(--blue-ll)",
      title: "AI resolution suggestions",
      body: "For every flagged issue, Audit PrOS suggests the right action — raise to client, resolve internally, or escalate. Every CA decision teaches the engine. No other audit tool does this.",
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--blue)"
          strokeWidth="2"
        >
          <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
    },
    {
      hl: false,
      bg: "#EEF2FF",
      title: "100+ adaptive audit rules",
      body: "Rules dynamically weighted by client industry. All 100+ rules run on every tier, always. Detection accuracy is never gated by plan.",
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--blue)"
          strokeWidth="2"
        >
          <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      ),
    },
    {
      hl: false,
      bg: "#EEF2FF",
      title: "100% ledger coverage",
      body: "Every transaction analysed — not 5%, not a sample. Eliminates sampling risk and strengthens your position during ICAI quality reviews.",
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--blue)"
          strokeWidth="2"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="M21 21l-4.35-4.35M11 8v3l2 2" />
        </svg>
      ),
    },
    {
      hl: false,
      bg: "#EEF2FF",
      title: "Source voucher linkage",
      body: "Each finding traces back to its exact voucher number, ledger account, date, and amount. Every flag is fully evidenced and audit-file ready.",
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--blue)"
          strokeWidth="2"
        >
          <path d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
        </svg>
      ),
    },
    {
      hl: false,
      bg: "#EEF2FF",
      title: "Confidence score per issue",
      body: "Every flagged item carries a confidence score derived from pattern consistency, statistical deviation, and the engine's growing knowledge.",
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--blue)"
          strokeWidth="2"
        >
          <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      hl: false,
      bg: "#EEF2FF",
      title: "Working paper export",
      body: "Generate an audit-ready working paper — exception schedule, risk categories, reviewer fields, and prepared-by signature. Directly into your audit file.",
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--blue)"
          strokeWidth="2"
        >
          <path d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
    },
  ],
  // NEW TAB — Tally accountant-specific features
  tally: [
    {
      hl: true,
      bg: "var(--blue-ll)",
      title: "Continuous ledger health checks",
      body: "Connect your Tally books and let Audit PrOS run 100+ checks automatically — every day, not just at year end. Catch errors before they compound.",
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--blue)"
          strokeWidth="2"
        >
          <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
    },
    {
      hl: false,
      bg: "#EEF2FF",
      title: "Tally XML one-click import",
      body: "Export your Tally data as XML — or use our real-time bridge agent — and Audit PrOS ingests every voucher, every ledger entry, every party in seconds.",
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--blue)"
          strokeWidth="2"
        >
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
        </svg>
      ),
    },
    {
      hl: false,
      bg: "#EEF2FF",
      title: "Entry error detection",
      body: "Duplicate vouchers, mismatched party names, unbooked payments, narration anomalies — flagged automatically with full source linkage before your CA even reviews.",
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--blue)"
          strokeWidth="2"
        >
          <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      ),
    },
    {
      hl: false,
      bg: "#EEF2FF",
      title: "GST mismatch alerts",
      body: "Spot GST entry errors in real time — wrong HSN codes, mismatched GSTIN on purchases, ITC eligibility issues — long before the filing deadline.",
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--blue)"
          strokeWidth="2"
        >
          <path d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
        </svg>
      ),
    },
    {
      hl: false,
      bg: "#EEF2FF",
      title: "Balance sheet sanity checks",
      body: "Closing balances, trial balance differences, negative stock, suspense ledger accumulation — flagged as issues so you can resolve them before month-end.",
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--blue)"
          strokeWidth="2"
        >
          <path d="M9 7H6a2 2 0 00-2 2v9a2 2 0 002 2h9a2 2 0 002-2v-3M16 3l3 3-7 7-4-1 1-4 7-5z" />
        </svg>
      ),
    },
    {
      hl: false,
      bg: "#EEF2FF",
      title: "Accountant confidence score",
      body: "Each issue comes with a confidence score and plain-English explanation. Spend time only on genuine problems — not noise. Present clean books to your CA.",
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--blue)"
          strokeWidth="2"
        >
          <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
  ],
  client: [
    {
      hl: true,
      bg: "var(--blue-ll)",
      title: "Live CA ↔ client sessions",
      body: "Both parties see the same query, Tally data, and AI observation. Close audit points in real time with timestamped notes and a signed record.",
      cs: "Audit Pro 360 — coming soon",
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--blue)"
          strokeWidth="2"
        >
          <path d="M15 10l4.553-2.069A1 1 0 0121 8.82v6.362a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
        </svg>
      ),
    },
    {
      hl: false,
      bg: "#EEF2FF",
      title: "Secure unique query link",
      body: "Send clients a time-limited, JWT-secured link to view and respond to audit queries. No account setup required. All tiers.",
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--blue)"
          strokeWidth="2"
        >
          <path d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
      ),
    },
    {
      hl: false,
      bg: "#EEF2FF",
      title: "PAN-based client identity",
      body: "Every client uniquely identified by PAN — no duplicates across CAs. One PAN, one record, one clean audit trail across all engagements.",
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--blue)"
          strokeWidth="2"
        >
          <path d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0" />
        </svg>
      ),
    },
    {
      hl: false,
      bg: "#EEF2FF",
      title: "Full client audit portal",
      body: "Corporate clients get a structured portal — all open queries, statuses, and what's needed from them. Mobile-friendly.",
      cs: "Audit Pro 360 — coming soon",
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--blue)"
          strokeWidth="2"
        >
          <rect x="2" y="3" width="20" height="14" rx="2" />
          <path d="M8 21h8m-4-4v4" />
        </svg>
      ),
    },
    {
      hl: false,
      bg: "#EEF2FF",
      title: "Threaded query responses",
      body: "Clients respond with text, upload supporting documents. All stored in a structured thread with timestamps.",
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--blue)"
          strokeWidth="2"
        >
          <path d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
      ),
    },
    {
      hl: false,
      bg: "#EEF2FF",
      title: "ICAI-verified CA identity",
      body: "Every CA registers with their ICAI Membership ID — validated against the live registry. Trust starts from the login.",
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--blue)"
          strokeWidth="2"
        >
          <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
    },
  ],
  compliance: [
    {
      hl: false,
      bg: "#EEF2FF",
      title: "Monthly filing dashboard",
      body: "See all GST and TDS filings across your portfolio — pending, filed, overdue — in one view. Never miss a deadline.",
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--blue)"
          strokeWidth="2"
        >
          <path d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
    },
    {
      hl: false,
      bg: "#EEF2FF",
      title: "GST tracking & reconciliation",
      body: "GSTR-2A vs books, GSTR-3B vs ledger, ITC mismatches — automatically reconciled from your Tally data. Compliance gaps surface before filing.",
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--blue)"
          strokeWidth="2"
        >
          <path d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
        </svg>
      ),
    },
    {
      hl: true,
      bg: "var(--blue-ll)",
      title: "File GST & TDS from platform",
      body: "File GSTR-1, GSTR-3B, and TDS returns directly — pre-populated from Tally data. No re-entry. No portal switching.",
      cs: "Audit Pro 360 — coming soon",
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--blue)"
          strokeWidth="2"
        >
          <path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      hl: false,
      bg: "#EEF2FF",
      title: "Deadline alerts",
      body: "Automated alerts for GST, TDS, and regulatory deadlines across your entire client portfolio. Configurable per client.",
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--blue)"
          strokeWidth="2"
        >
          <path d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      ),
    },
    {
      hl: false,
      bg: "#EEF2FF",
      title: "Client risk dashboard",
      body: "Portfolio-level view — flag high-risk clients, track audit completion rates, identify which engagements need urgent attention.",
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--blue)"
          strokeWidth="2"
        >
          <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
    },
    {
      hl: false,
      bg: "#EEF2FF",
      title: "Credit report generation",
      body: "Generate lending-ready credit reports from audit data — a new revenue stream for your CA firm and high-value deliverable for MSME clients.",
      cs: "Coming in Phase 3",
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--blue)"
          strokeWidth="2"
        >
          <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
    },
  ],
  team: [
    {
      hl: false,
      bg: "#EEF2FF",
      title: "Team email logins",
      body: "Multiple email logins, role-based access — Admin for partners, Member for staff and accountants. Starter: 2, Audit Pro: 10, Audit Pro 360: unlimited.",
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--blue)"
          strokeWidth="2"
        >
          <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
    },
    {
      hl: false,
      bg: "#EEF2FF",
      title: "Bulk query management",
      body: "Raise, track, and close queries across all clients in one view. Filter by status, assign to team members, send bulk reminders.",
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--blue)"
          strokeWidth="2"
        >
          <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      ),
    },
    {
      hl: false,
      bg: "#EEF2FF",
      title: "Audit status tracking",
      body: "Track every engagement from Tally import to audit closure — stage-by-stage visibility for partners, staff, and clients.",
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--blue)"
          strokeWidth="2"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      ),
    },
    {
      hl: false,
      bg: "#EEF2FF",
      title: "Smart notifications",
      body: "Email and in-app alerts when a file is processed, a client responds, a deadline approaches, or a high-confidence issue needs partner attention.",
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--blue)"
          strokeWidth="2"
        >
          <path d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      ),
    },
    {
      hl: true,
      bg: "var(--blue-ll)",
      title: "Audit Copilot",
      body: "Ask your client data questions in plain language. Show high-value entries posted after 10 PM. Find reversals within 3 days. Every answer is sourced and cited.",
      cs: "Audit Pro 360 — coming soon",
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--blue)"
          strokeWidth="2"
        >
          <path d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
      ),
    },
    {
      hl: false,
      bg: "#EEF2FF",
      title: "Tally XML import",
      body: "One-click import from Tally ERP and TallyPrime. Full ledger ingested instantly — every voucher, every entry — AI analysis begins automatically.",
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--blue)"
          strokeWidth="2"
        >
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
        </svg>
      ),
    },
  ],
};

// ─── CONTACT US ───────────────────────────────────────────────────────────────
function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [error, setError] = useState("");

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus("error");
      setError("Please fill in your name, email, and message.");
      return;
    }
    setStatus("sending");
    setError("");
    try {
      await api.submitContactQuery(form);
      setStatus("sent");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      setStatus("error");
      setError(err.message || "Could not send your message. Please try again.");
    }
  };

  return (
    <div className="lp-section" id="lp-contact">
      <div className="lp-section-eyebrow">Get in touch</div>
      <h2 className="lp-section-title">Contact us</h2>
      <p className="lp-section-sub">
        Questions about Audit PrOS, onboarding, or partnerships? Reach out directly
        or send us a query below — we typically respond within one business day.
      </p>
      <div className="lp-contact-grid">
        <div className="lp-contact-info">
          <div className="lp-contact-info-item">
            <div className="lp-contact-info-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16v16H4z" opacity="0" />
                <path d="M22 6l-10 7L2 6" />
                <path d="M2 6h20v12H2z" />
              </svg>
            </div>
            <div>
              <div className="lp-contact-info-label">Email</div>
              <a className="lp-contact-info-value" href="mailto:Sameep@speedstar.ai">Sameep@speedstar.ai</a>
            </div>
          </div>
          <div className="lp-contact-info-item">
            <div className="lp-contact-info-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
              </svg>
            </div>
            <div>
              <div className="lp-contact-info-label">Phone</div>
              <a className="lp-contact-info-value" href="tel:+918108506999">+91 81085 06999</a>
            </div>
          </div>
        </div>

        <form className="lp-contact-form" onSubmit={handleSubmit}>
          {status === "sent" ? (
            <div className="lp-success-box" style={{ padding: "40px 0" }}>
              <div className="lp-success-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="var(--blue)" strokeWidth="2.5" strokeLinecap="round" width="26" height="26">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </div>
              <div className="lp-success-title">Message sent</div>
              <p className="lp-success-sub">Thanks for reaching out — we'll get back to you shortly.</p>
              <button type="button" className="lp-google-btn" style={{ marginTop: 16 }} onClick={() => setStatus("idle")}>
                Send another message
              </button>
            </div>
          ) : (
            <>
              {status === "error" && <div className="lp-error-box">{error}</div>}
              <div className="lp-contact-form-row">
                <div className="lp-field">
                  <label>Your name</label>
                  <div className="lp-field-wrap">
                    <input type="text" placeholder="Jane Doe" value={form.name} onChange={update("name")} />
                  </div>
                </div>
                <div className="lp-field">
                  <label>Email</label>
                  <div className="lp-field-wrap">
                    <input type="email" placeholder="you@company.com" value={form.email} onChange={update("email")} />
                  </div>
                </div>
              </div>
              <div className="lp-field">
                <label>Phone (optional)</label>
                <div className="lp-field-wrap">
                  <input type="tel" placeholder="98765 43210" value={form.phone} onChange={update("phone")} />
                </div>
              </div>
              <div className="lp-field">
                <label>Message</label>
                <div className="lp-field-wrap lp-field-wrap-textarea">
                  <textarea
                    rows={4}
                    placeholder="How can we help?"
                    value={form.message}
                    onChange={update("message")}
                  />
                </div>
              </div>
              <button className="lp-primary-btn" type="submit" disabled={status === "sending"}>
                {status === "sending" ? <span className="lp-spinner" /> : null}
                {status === "sending" ? "Sending…" : "Send message →"}
              </button>
            </>
          )}
        </form>
      </div>
    </div>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function LoginPage({ onAuthenticated, forceSwitch = false }) {
  const [success, setSuccess] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showLegal, setShowLegal] = useState(null);
  const [featTab, setFeatTab] = useState("ai");

  const handleSuccess = (info) => {
    setSuccess(info);
    window.gtag?.('event', 'conversion', {
      send_to: 'AW-18210439066/JZkJCLmdurgcEJr_tOtD',
      value: 1.0,
      currency: 'INR',
    });
    setTimeout(() => {
      setShowProfile(true);
      onAuthenticated?.(info);
    }, 1400);
  };
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.pageYOffset - 60;
    window.scrollTo({ top, behavior: "smooth" });
  };

  const _today = new Date();
  _today.setHours(0, 0, 0, 0);
  const navDaysLeft = Math.max(0, Math.ceil((AUDIT_DEADLINE.getTime() - _today.getTime()) / (1000 * 60 * 60 * 24)));
  const deadlineDisplay = AUDIT_DEADLINE.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

  if (showHelp) return <HelpPage onBack={() => setShowHelp(false)} />;
  if (showLegal) return <LegalPage type={showLegal} onBack={() => setShowLegal(null)} />;

  return (
    <>
      <div className="lp-root">
        <IndianFlagWatermark />
        {/* NAV */}
        <nav className="lp-nav">
          <a className="lp-nav-logo" href="#">
            <div className="lp-nav-logo-mark">
              <LogoIcon />
            </div>
            <span className="lp-nav-brand">
              Audit&nbsp;<span className="lp-nav-pros-badge">PrOS</span>
            </span>
            <span className="lp-nav-sep">-</span>
            <span className="lp-nav-tagline">
              Smarter Audits. Cleaner Books.
            </span>
          </a>
          <div className="lp-nav-right">
            <button
              className="lp-nav-link"
              onClick={() => scrollTo("lp-features")}
            >
              Features
            </button>
            <button
              className="lp-btn lp-btn-primary"
              onClick={() => scrollTo("lp-signin")}
            >
              Sign in →
            </button>
          </div>
        </nav>

        {/* HERO */}
        <section className="lp-hero" id="lp-login">
          <div>
            <div className="lp-hero-eyebrow">
              <span className="lp-eyebrow-dot" />
              Beta launch — for CA's, clients &amp; accountants
            </div>
            <h1 className="lp-h1">
              <span className="lp-h1-line">Audit Smarter.</span>
              <span className="lp-h1-line">Book Cleaner.</span>
              <span className="lp-h1-line">
                <em>Close Faster.</em>
              </span>
            </h1>
            <p className="lp-hero-sub">
              Audit PrOS is the operating system for the entire audit
              relationship — giving CA's AI-powered audit intelligence and
              giving clients and their accountants the tools to keep books
              clean, accurate, and audit-ready year-round.
            </p>

            {/* Audience pills */}
            <div className="lp-hero-audience">
              <span className="lp-audience-pill">
                <svg width="8" height="8" viewBox="0 0 8 8">
                  <circle cx="4" cy="4" r="4" fill="#1447E6" />
                </svg>
                For Chartered Accountants
              </span>
              <span className="lp-audience-pill dark">
                <svg width="8" height="8" viewBox="0 0 8 8">
                  <circle cx="4" cy="4" r="4" fill="white" />
                </svg>
                For Businesses &amp; Accountants
              </span>
            </div>

            <div className="lp-hero-actions">
              <button
                className="lp-btn lp-btn-primary lp-btn-lg"
                onClick={() => scrollTo("lp-how")}
              >
                How it works →
              </button>
              <button
                className="lp-btn-outline lp-btn-lg"
                onClick={() => scrollTo("lp-features")}
              >
                See features
              </button>
            </div>
          </div>

          <div id="lp-signin">
            {success ? (
              <div className="lp-login-card">
                <div className="lp-success-box">
                  <div className="lp-success-icon">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="var(--blue)"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      width="26"
                      height="26"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  </div>
                  <div className="lp-success-title">You're signed in</div>
                  <p className="lp-success-sub">
                    {success.method === "gmail"
                      ? `Welcome, ${success.account?.name?.split(" ")[0] ?? ""}.`
                      : "Mobile verified."}
                    &nbsp;Opening your profile…
                  </p>
                </div>
              </div>
            ) : (
              <LoginCard
                forceSwitch={forceSwitch}
                onSuccess={handleSuccess}
                onShowHelp={() => setShowHelp(true)}
              />
            )}
            <div className="lp-hero-countdown-block">
              <div className="lp-hero-countdown-row">
                <span className="lp-hero-countdown-num">{navDaysLeft}</span>
                <span className="lp-hero-countdown-label">days remaining for Audit Closure</span>
              </div>
              <div className="lp-hero-countdown-notice">
                Don't miss {deadlineDisplay} deadline.&nbsp;<strong>Act Now.</strong>
              </div>
            </div>
          </div>
        </section>

        {/* AUDIENCE CARDS */}
        <div className="lp-audience-cards">
          <div className="lp-audience-cards-inner">
            {/* CA Card */}
            <div className="lp-aud-card ca">
              <div className="lp-aud-card-head">
                <div className="lp-aud-card-icon ca">
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <div className="lp-aud-card-eyebrow">
                    For Chartered Accountants
                  </div>
                  <div className="lp-aud-card-title">AI Audit Assistant</div>
                </div>
              </div>
              <div className="lp-aud-card-body">
                Import Tally data, run 100+ adaptive AI audit checks, surface
                every issue with source evidence, and close client queries — all
                in one platform. Audit faster without compromising quality.
                <div className="lp-aud-ca-pitch">
                  For CA firms: Deliver 10x more reports with the same staff. Collaborate with your clients in real time with audit trace.
                </div>
              </div>
            </div>

            {/* Business & Accountant Card */}
            <div className="lp-aud-card biz">
              <div className="lp-aud-card-head">
                <div className="lp-aud-card-icon biz">
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div>
                  <div className="lp-aud-card-eyebrow">
                    For Businesses &amp; Accountants
                  </div>
                  <div className="lp-aud-card-title">
                    Clean Accounting Assistant
                  </div>
                </div>
              </div>
              <div className="lp-aud-card-body">
                Run the same checks your CA runs — on your own books, any time
                of year. Spot duplicates, gaps, and anomalies in plain language.
                Arrive at every audit with clean, verified data and no
                surprises.
              </div>
            </div>
          </div>
        </div>

        {/* THE PROBLEM */}
        <div className="lp-problem-full">
          <div className="lp-problem-full-inner">
            <span className="lp-slide-badge problem">THE PROBLEM</span>
            <h2 className="lp-problem-full-title">The old way of getting audited is broken</h2>
            <ul className="lp-problem-full-list">
              <li>Slower audit process running into days</li>
              <li>Manual back-and-forth on documents</li>
              <li>No visibility into where things stand</li>
              <li>Deadline missed. Penalty paid.</li>
              <li>No audit trail about who did what and when</li>
              <li>Subjective auditing with manual review</li>
              <li>Errors caught too late — penalties unavoidable</li>
              <li>Documents scattered across emails and drives</li>
            </ul>
          </div>
        </div>

        {/* STATS */}
        <div className="lp-stats">
          <div className="lp-stats-inner">
            {[
              { num: "200+", lbl: "CA firms in beta" },
              { num: "100+", lbl: "Adaptive audit rules" },
              { num: "100%", lbl: "Tally ledger covered" },
              { num: "90%", lbl: "Time saved per audit" },
              { num: "Year-round", lbl: "Books health checks" },
            ].map((s, i, arr) => (
              <div key={s.lbl} style={{ display: "contents" }}>
                <div className="lp-stat">
                  <div className="lp-stat-num">
                    <span>{s.num}</span>
                  </div>
                  <div className="lp-stat-lbl">{s.lbl}</div>
                </div>
                {i < arr.length - 1 && <div className="lp-stat-div" />}
              </div>
            ))}
          </div>
        </div>

        {/* FEATURES */}
        <div className="lp-section" id="lp-features">
          <div className="lp-section-eyebrow">Why Audit PrOS is a game changer?</div>
          <h2 className="lp-section-title">
            One platform for the entire
            <br />
            audit relationship.
          </h2>
          <p className="lp-section-sub">
            From Tally import to audit closure — and from year-round books
            health checks to clean data handoff. Every step of the engagement,
            for every party involved.
          </p>
          <div className="lp-feat-tabs">
            {FEAT_TABS.map((t) => (
              <button
                key={t.id}
                className={`lp-feat-tab${featTab === t.id ? " active" : ""}`}
                onClick={() => setFeatTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="lp-feat-grid">
            {FEAT_PANELS[featTab].map((c, i) => (
              <div
                key={c.title}
                className={`lp-feat-card${c.hl ? " hl" : ""}`}
                style={{ animationDelay: `${i * 0.05}s` }}
              >
                <div className="lp-feat-icon" style={{ background: c.bg }}>
                  {c.icon}
                </div>
                <div className="lp-feat-card-title">{c.title}</div>
                <div className="lp-feat-card-body">{c.body}</div>
                {c.cs && <span className="lp-coming-soon">{c.cs}</span>}
              </div>
            ))}
          </div>
        </div>

        {/* HOW IT WORKS */}
        <div className="lp-how" id="lp-how">
          <div className="lp-how-inner">
            <div className="lp-how-eyebrow">How it works</div>
            <h2 className="lp-how-title">
              Tally import to <em>audit closed</em>
              <br />— in one platform
            </h2>
            <p className="lp-how-sub">
              Works for both CAs running audits and accountants keeping books
              clean daily. The entire workflow lives here.
            </p>
            <div className="lp-steps-grid">
              {[
                {
                  n: "01",
                  title: "Connect Tally",
                  body: "Upload Tally XML or install our bridge agent for live sync. Full ledger ingested — every voucher, every entry — analysis begins immediately.",
                  arrow: true,
                  icon: (
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                    >
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
                    </svg>
                  ),
                },
                {
                  n: "02",
                  title: "AI runs 100+ checks",
                  body: "Rules weighted by industry and role. Every issue linked to source voucher with confidence score — whether you're an accountant or a CA reviewer.",
                  arrow: true,
                  icon: (
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                    >
                      <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  ),
                },
                {
                  n: "03",
                  title: "Resolve or escalate",
                  body: "Accountants fix entry-level issues. CAs raise audit queries to clients via secure link. Each party sees exactly what's relevant to them.",
                  arrow: true,
                  icon: (
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                    >
                      <path d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                  ),
                },
                {
                  n: "04",
                  title: "Close audit — file returns",
                  body: "Every point closed with a full signed trail. File GST and TDS returns directly — pre-populated from the Tally data already analysed.",
                  arrow: false,
                  icon: (
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                    >
                      <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  ),
                },
              ].map((s) => (
                <div className="lp-step" key={s.n}>
                  <div className="lp-step-num">{s.n}</div>
                  <div className="lp-step-icon">{s.icon}</div>
                  <div className="lp-step-title">{s.title}</div>
                  <div className="lp-step-body">{s.body}</div>
                  {s.arrow && (
                    <div className="lp-step-arrow">
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="white"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                      >
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* GET ACCESS */}
        <div className="lp-access" id="lp-access">
          <div className="lp-access-inner">
            <div className="lp-section-eyebrow">Getting started</div>
            <h2 className="lp-section-title" style={{ color: "var(--ink)" }}>
              How CAs, Accountants &amp; Clients get access
            </h2>
            <p className="lp-section-sub">
              Separate onboarding flows for each role — all starting with a
              single sign-in. No complex setup required.
            </p>
            <div className="lp-access-cols">
              {/* CA Column */}
              <div className="lp-access-col">
                <div className="lp-access-col-head">
                  <div className="lp-access-col-icon">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                    >
                      <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <div>
                    <div className="lp-access-col-title">
                      Chartered Accountants
                    </div>
                    <div className="lp-access-col-sub">
                      3 steps · ICAI-verified · full audit platform
                    </div>
                  </div>
                </div>
                <div className="lp-access-steps">
                  {[
                    {
                      title: "Register via Google SSO",
                      desc: (
                        <>
                          Sign up with your Google account. Default role is{" "}
                          <code>customer</code> — takes 30 seconds.
                        </>
                      ),
                    },
                    {
                      title: "Request CA upgrade",
                      desc: (
                        <>
                          Go to Profile → <strong>Become a CA</strong>. Enter
                          your 6-digit ICAI membership number and upload
                          supporting documents.
                        </>
                      ),
                    },
                    {
                      title: "Admin approves → full CA access",
                      desc: (
                        <>
                          Admin reviews your request in the CA Requests queue
                          and approves. Your role flips to <code>ca</code> and
                          you get full audit platform access.
                        </>
                      ),
                    },
                  ].map((step, i, arr) => (
                    <div className="lp-access-step" key={i}>
                      <div className="lp-access-step-left">
                        <div className="lp-access-step-num">{i + 1}</div>
                        {i < arr.length - 1 && (
                          <div className="lp-access-step-line" />
                        )}
                      </div>
                      <div className="lp-access-step-body">
                        <div className="lp-access-step-title">{step.title}</div>
                        <div className="lp-access-step-desc">{step.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Client / Accountant Column */}
              <div className="lp-access-col">
                <div className="lp-access-col-head">
                  <div className="lp-access-col-icon client">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                    >
                      <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <div>
                    <div className="lp-access-col-title">
                      Tally Accountants &amp; MSME Clients
                    </div>
                    <div className="lp-access-col-sub">
                      2 ways to get access
                    </div>
                  </div>
                </div>

                {/* Way 1 */}
                <div className="lp-access-way">
                  <div className="lp-access-way-label-plain">
                    Way 1 — Via your CA
                  </div>
                  <div className="lp-access-steps">
                    {[
                      {
                        title: "CA registers you",
                        desc: "Your CA adds you as a client inside Audit PrOS and sends an email invite.",
                      },
                      {
                        title: "Sign up via SSO",
                        desc: "Click the invite link, sign in with Google — you're automatically linked to your CA's workspace.",
                      },
                      {
                        title: "CA controls your access",
                        desc: "Your CA decides which pages, reports, and issues are visible to you. No setup required on your end.",
                      },
                    ].map((step, i, arr) => (
                      <div className="lp-access-step" key={i}>
                        <div className="lp-access-step-left">
                          <div className="lp-access-step-num dark">{i + 1}</div>
                          {i < arr.length - 1 && (
                            <div className="lp-access-step-line" />
                          )}
                        </div>
                        <div className="lp-access-step-body">
                          <div className="lp-access-step-title">
                            {step.title}
                          </div>
                          <div className="lp-access-step-desc">{step.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Way 2 */}
                <div
                  className="lp-access-way"
                  style={{
                    marginTop: 20,
                    paddingTop: 20,
                    borderTop: "1px solid var(--line)",
                  }}
                >
                  <div className="lp-access-way-label-plain">
                    Way 2 — Direct sign-up (for Tally accountants &amp; MSMEs) <span className="lp-access-way-badge lp-access-way-badge--nowrap">Most common</span>
                  </div>
                  <div className="lp-access-steps">
                    {[
                      {
                        title: "Sign up via Google SSO",
                        desc: (
                          <>
                            <code>customer</code> role assigned by default —
                            takes 30 seconds.
                          </>
                        ),
                      },
                      {
                        title: "Select a plan",
                        desc: "Go to Subscription → choose a CA plan (if you manage accounts for multiple clients) or a Client plan (for your own company books).",
                      },
                      {
                        title: "Install Tally Bridge Agent",
                        desc: "Download the bridge agent, configure it to your Tally — one-time setup. Or export XML manually at any time.",
                      },
                      {
                        title: "Raise request for Audit with Audit PrOS",
                        desc: "Get audited with Audit PrOS inhouse auditors and collaborate with them to close your audit the professional way.",
                      },
                    ].map((step, i, arr) => (
                      <div className="lp-access-step" key={i}>
                        <div className="lp-access-step-left">
                          <div className="lp-access-step-num dark">{i + 1}</div>
                          {i < arr.length - 1 && (
                            <div className="lp-access-step-line" />
                          )}
                        </div>
                        <div className="lp-access-step-body">
                          <div className="lp-access-step-title">
                            {step.title}
                          </div>
                          <div className="lp-access-step-desc">{step.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CONTACT */}
        <ContactSection />

        {/* BOTTOM CTA */}
        <div className="lp-cta-section">
          <div className="lp-cta-inner">
            <div className="lp-cta-eyebrow">Beta launch — limited seats</div>
            <h2 className="lp-cta-title">
              For CAs, accountants
              <br />
              and the businesses they serve
            </h2>
            <div className="lp-cta-deadline">
              Don't miss {deadlineDisplay} deadline.&nbsp;<strong>Act Now.</strong>
            </div>
            <div className="lp-cta-actions">
              <button
                className="lp-cta-btn lp-cta-btn-white"
                onClick={() => scrollTo("lp-signin")}
              >
                Start free trial →
              </button>
            </div>
            <p className="lp-cta-note">
              ICAI Membership ID verified for CA accounts. Tally integration
              works with ERP9 and TallyPrime.
              <br />
              By signing in you agree to our{" "}
              <a style={{color:"rgba(255,255,255,0.7)",cursor:"pointer"}} onClick={(e)=>{e.preventDefault();setShowLegal("terms")}} href="#">Terms of Service</a>
              {" "}and{" "}
              <a style={{color:"rgba(255,255,255,0.7)",cursor:"pointer"}} onClick={(e)=>{e.preventDefault();setShowLegal("privacy")}} href="#">Privacy Policy</a>.
            </p>
          </div>
        </div>

        {/* FOOTER */}
        <footer className="lp-footer">
          <span className="lp-footer-brand">Audit PrOS</span>
          <span className="lp-footer-mid">
            © 2026 Speedstar AI Labs Private Limited, Bangalore, Karnataka, India
          </span>
          <div className="lp-footer-links">
            <button className="lp-footer-link-btn" onClick={() => scrollTo("lp-contact")}>Contact</button>
            <a onClick={(e) => { e.preventDefault(); setShowLegal("privacy"); }} href="#">Privacy Policy</a>
            <a onClick={(e) => { e.preventDefault(); setShowLegal("terms"); }} href="#">Terms of Service</a>
            <a
              onClick={(e) => {
                e.preventDefault();
                setShowHelp(true);
              }}
            >
              Help
            </a>
          </div>
        </footer>

        {/* PROFILE */}
        {showProfile && (
          <Profile
            userInfo={success}
            onClose={() => setShowProfile(false)}
            onSwitchAccount={() => {
              setShowProfile(false);
              setSuccess(null);
            }}
            onRoleUpgrade={(updated) =>
              console.log("[Audit PrOS] Role updated:", updated)
            }
          />
        )}
      </div>
    </>
  );
}
